import type { ComponentProps } from 'react';
import { Component, useCallback, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import type { DateRangeProps, Range, RangeKeyDict } from 'react-date-range';
import { DateRange } from 'react-date-range';
import { generateStyles } from 'react-date-range/dist/utils';
import coreStyles from 'react-date-range/dist/styles';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import {
    format,
    getDayOfYear,
    getMonth,
    getYear,
    setDayOfYear,
    setMilliseconds,
    setMonth,
    setSeconds,
    setYear,
} from 'date-fns';
import { debounce } from 'lodash-es';
import profile from '../profile';
import { startOfOmnidots } from './date-fns';
import {
    applyTimeString,
    DAY_END_TIME,
    DAY_START_TIME,
    ensureChronologicalDateRange,
} from './date-range';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

type TimeString = string;
type DateKey = 'startDate' | 'endDate';
type RequiredRange = { [key in DateKey]: Date };

interface AllDayCheckboxProps {
    getCurrent: () => Date;
    timeWhenChecked: TimeString;
    checkboxLabel: string;
    changeTime: (time: TimeString) => void;
}

type TimePickerProps = ComponentProps<typeof TimePicker>;

interface TimePickerDebouncedProps
    extends Omit<TimePickerProps, 'onChange' | 'value' | 'onClockClose'> {
    value: Date;
    onChange: (timeString: TimeString) => void;
}

interface DateTimeRangeState {
    uncheckTime: Record<string, TimeString>;
}

interface DateTimeRangeProps extends Omit<DateRangeProps, 'ranges' | 'onChange' | 'classNames'> {
    ranges: [Range];
    onChange: (ranges: RangeKeyDict) => void;
    classNames?: DateRangeProps['classNames'];
}

function dateToTimeString(date: Date): TimeString {
    return format(date, 'HH:mm');
}

function transferDatePartToDate(newDate: Date, existingDate: Date): Date {
    return setDayOfYear(
        setMonth(setYear(existingDate, getYear(newDate)), getMonth(newDate)),
        getDayOfYear(newDate)
    );
}

function AllDayCheckbox({
    getCurrent,
    timeWhenChecked,
    checkboxLabel,
    changeTime,
}: AllDayCheckboxProps): JSX.Element {
    // Default value 12:00 just because it's nice in the middle of
    // both checked values.
    const [uncheckTime, setUncheckTime] = useState<TimeString>('12:00');

    return (
        <div className="all-day-checkbox-wrapper">
            <label>
                <input
                    type="checkbox"
                    onChange={(event) => {
                        if (event.target.checked) {
                            // Set the time to the 'timeWhenChecked'.
                            setUncheckTime(dateToTimeString(getCurrent()));
                            changeTime(timeWhenChecked);
                        } else {
                            // Unchecking with no dateBeforeChecked
                            // sets the date to 12:00. 12:00 just
                            // because it's nice in the middle of
                            // both checked values.
                            changeTime(uncheckTime);
                        }
                    }}
                    checked={dateToTimeString(getCurrent()) === timeWhenChecked}
                />
                {checkboxLabel}
            </label>
        </div>
    );
}

// This timepicker will only emit an onChange after a debounce of 300ms or when losing
// focus. This is in contrast to the normal timepicker behaviour that emits an onChange
// with every adjustment immediately.
function TimePickerDebounced({ value, onChange, ...props }: TimePickerDebouncedProps): JSX.Element {
    const changedValueRef = useRef<TimeString | null>(null);

    const emitChange = useCallback(() => {
        if (changedValueRef.current) {
            onChange(changedValueRef.current);
        }
    }, [onChange]);

    const emitChangeDebounced = useMemo(() => debounce(emitChange, 300), [emitChange]);

    return (
        <TimePicker
            onChange={(timeString: TimeString | null) => {
                changedValueRef.current = timeString;
                emitChangeDebounced();
            }}
            value={value}
            // When losing focus (tab or click outside) we want to skip the debounce.
            onClockClose={emitChange}
            {...props}
        />
    );
}

class DateTimeRange extends Component<DateTimeRangeProps, DateTimeRangeState> {
    private styles: Record<string, string>;

    constructor(props: DateTimeRangeProps) {
        super(props);
        this.state = {
            uncheckTime: {},
        };
        this.styles = generateStyles([
            coreStyles,
            (props.classNames as Record<string, string> | undefined) ?? {},
        ]);
    }

    setUncheckTime(slot: string, time: TimeString): void {
        this.setState((state) => ({
            uncheckTime: {
                ...state.uncheckTime,
                [slot]: time,
            },
        }));
    }

    modifyDate(modifier: (range: Range) => RequiredRange): void {
        const [selection] = this.props.ranges;
        const newState = modifier(selection);

        // Truncate the start and end date to the first/last milliseconds respectively.
        newState.startDate = setSeconds(newState.startDate, 0);
        newState.endDate = setMilliseconds(setSeconds(newState.endDate, 59), 999);

        const correctedRange = ensureChronologicalDateRange(newState);
        newState.startDate = correctedRange.startDate;
        newState.endDate = correctedRange.endDate;

        this.props.onChange({
            selection: {
                key: 'selection',
                ...newState,
            },
        });
    }

    changeTime(dateKey: DateKey, timeString: TimeString): void {
        this.modifyDate((oldRange) => {
            const currentDate = oldRange[dateKey]!;

            return {
                ...oldRange,
                [dateKey]: applyTimeString(currentDate, timeString),
            } as RequiredRange;
        });
    }

    getCurrent(dateKey: DateKey): Date {
        const current = this.props.ranges[0][dateKey];
        if (!current) {
            throw new Error(`DateTimeRange: Missing ${dateKey} in ranges prop`);
        }
        return current;
    }

    render(): JSX.Element {
        return (
            <div
                className={classnames(
                    'rdrDateTimeRangeWrapper',
                    this.styles.dateRangePickerWrapper
                )}
            >
                <DateRange
                    {...this.props}
                    className={this.props.className}
                    minDate={startOfOmnidots}
                    maxDate={new Date()}
                    onChange={(newDate: RangeKeyDict) => {
                        const { selection } = newDate as { selection: Range };

                        // Every onChange from the DateRange picker resets the time
                        // to 00:00:00. We want the time to remain unchanged when
                        // only a date is selected. Therefore, we copy the time from
                        // the originally selected date range to the new date range.
                        this.modifyDate(
                            (oldRange) =>
                                Object.fromEntries(
                                    (['startDate', 'endDate'] as const).map((dateKey) => [
                                        dateKey,
                                        transferDatePartToDate(
                                            selection[dateKey]!,
                                            oldRange[dateKey]!
                                        ),
                                    ])
                                ) as RequiredRange
                        );
                    }}
                />
                <div className="time-range-picker-wrapper">
                    {(
                        [
                            ['startDate', gettext('START_OF_DAY'), DAY_START_TIME],
                            ['endDate', gettext('END_OF_DAY'), DAY_END_TIME],
                        ] as Array<[DateKey, string, TimeString]>
                    ).map(([dateKey, checkboxLabel, timeWhenChecked], index) => (
                        <div className="time-range-picker-item" key={index}>
                            <TimePickerDebounced
                                onChange={(timeString) => {
                                    this.changeTime(dateKey, timeString);
                                }}
                                value={this.getCurrent(dateKey)}
                                disableClock
                                format={profile.use24HourNotation ? 'HH:mm' : 'hh:mm a'}
                                clearIcon={null}
                            />

                            <AllDayCheckbox
                                getCurrent={() => this.getCurrent(dateKey)}
                                changeTime={(value) => {
                                    this.changeTime(dateKey, value);
                                }}
                                checkboxLabel={checkboxLabel}
                                timeWhenChecked={timeWhenChecked}
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default DateTimeRange;
